import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import echarts from 'echarts/lib/echarts';
// Import renderer, note that introducing the CanvasRenderer or SVGRenderer is a required step!
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { addDot } from '../../utils/index';
import { responsive } from '../../resources/constantes.json';
import AnimatedNumber from '../AnimatedNumber';
import { LayoutGroup, motion } from 'framer-motion';
import useMeasure from 'use-measure';
import useId from 'hooks/useId';
import useWindowWidth from 'hooks/useWindowWidth';

function singleDonut(name, porc) {
  const options = {
    name: name,
    color: ['#FFD01C', 'transparent'],
    type: 'pie',
    radius: [`50%`, '90%'],
    avoidLabelOverlap: false,
    itemStyle: {
      borderColor: '#fff',
      borderWidth: 0,
    },
    label: {
      show: false,
      position: 'center',
    },
    emphasis: {
      label: {
        show: false,
      },
      scaleSize: 1,
      itemStyle: {
        borderColor: '#fff',
        borderWidth: 0,
      },
    },
    silent: true,
    labelLine: {
      show: false,
    },
    stillShowZeroSum: false,
    data: [{ value: porc, name: name }, { value: 100 - porc, name: '' }],
  };
  return { series: options };
}

function grayDonut(parcial) {
  const options = {
    color: ['#D8DFEA'],
    type: 'pie',
    radius: parcial === 0 ? [`60%`, '90%'] : [`60%`, '80%'],
    avoidLabelOverlap: false,
    itemStyle: {
      borderColor: '#fff',
      borderWidth: 0,
    },
    label: {
      show: false,
      position: 'center',
    },
    emphasis: {
      label: {
        show: false,
      },
      scaleSize: 1,
      itemStyle: {
        borderColor: '#fff',
        borderWidth: 0,
      },
    },
    silent: true,
    animation: false,
    labelLine: {
      show: false,
    },
    stillShowZeroSum: false,
    data: [{ value: 1 }],
  };
  return { series: options };
}

const variants = {
  hidden: {
    scale: 0,
  },
  shown: {
    scale: 1,
  },
};

const DataDonut = ({
  title,
  porc,
  parcial,
  total,
  collapseFirstRow = false,
  hideDonut = false,
}) => {
  const [_collapseFirstRow, setCollapseFirstRow] = useState(false);
  const ref = useRef();
  const id = useId();
  const { height } = useMeasure(ref);
  const tmRef = useRef(null);
  const p = Number(porc.replace(',', '.').replace('%', ''));
  const windowWidth = useWindowWidth().width;

  useEffect(() => {
    if (!collapseFirstRow) {
      tmRef.current = setTimeout(() => setCollapseFirstRow(false), 200);
    } else {
      setCollapseFirstRow(true);
    }
    return () => {
      if (tmRef.current) {
        clearTimeout(tmRef.current);
        tmRef.current = null;
      }
    };
  }, [collapseFirstRow]);

  return (
    <DonutContainer
      animate={{ gridTemplateColumns: hideDonut ? '0 1fr' : 'auto 1fr' }}
      initial={{ gridTemplateColumns: 'auto 1fr' }}
    >
      <EchartContainer
        variants={variants}
        style={{
          overflow: hideDonut ? 'hidden' : 'visible',
        }}
        transition={{ duration: 0.2 }}
        animate={hideDonut ? 'hidden' : 'shown'}
        initial="hidden"
      >
        <div key={windowWidth} style={{ position: 'relative' }}>
          <ReactEChartsCore
            echarts={echarts}
            option={singleDonut(title, p)}
            style={{
              height: '10em',
              width: '100%',
              zIndex: 2,
              position: 'relative',
            }}
          />
          <ReactEChartsCore
            echarts={echarts}
            option={grayDonut(parcial >= total ? total : parcial)}
            style={{
              height: '10em',
              width: '100%',
              zIndex: 1,
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          />
        </div>
      </EchartContainer>
      <DataContainer
        animate={{
          marginLeft: hideDonut
            ? '0em'
            : windowWidth < responsive.maxWidth.tablet
            ? '1.5em'
            : '3em',
        }}
        collapseFirstRow={_collapseFirstRow}
      >
        <FirstRow
          animate={{
            gridTemplateColumns: _collapseFirstRow ? 'auto 0fr' : 'auto 1fr',
            gridTemplateRows: `1fr ${_collapseFirstRow ? height : 0}px`,
          }}
          transition={{ duration: 0.2 }}
        >
          <Title collapseFirstRow={_collapseFirstRow}>{title}</Title>
          {_collapseFirstRow ? <div /> : null}
          <LayoutGroup>
            <Subtitle
              ref={ref}
              collapseFirstRow={_collapseFirstRow}
              style={{
                textAlign: _collapseFirstRow ? 'start' : 'end',
              }}
              transition={{ duration: 0.2 }}
              layoutId={id}
            >
              <AnimatedNumber value={p} percentage />
            </Subtitle>
          </LayoutGroup>
        </FirstRow>
        <SecondRow collapseFirstRow={_collapseFirstRow}>
          <AnimatedNumber value={parcial} formatter={addDot} integer />
          <span>{' de '}</span>
          <AnimatedNumber value={total} formatter={addDot} integer />
        </SecondRow>
      </DataContainer>
    </DonutContainer>
  );
};

const DonutContainer = styled(motion.div)`
  display: grid;
  font-family: Montserrat;
  color: #164192;
  height: 100%;
  padding: 0 2em;
  @media screen and (max-width: ${responsive.tablet}) {
    padding: 0;
  }
`;

const EchartContainer = styled(motion.div)`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  // overflow: hidden;
`;

const DataContainer = styled(motion.div)`
  display: grid;
  grid-template-rows: ${({ collapseFirstRow }) =>
    collapseFirstRow ? 'minmax(50%, min-content) 1fr' : 'min-content auto'};
  margin-left: 3em;
  @media screen and (max-width: ${responsive.tablet}px) {
    margin-left: 0 0.8em;
  }
  @media screen and (max-width: ${responsive.mobile}px) {
    margin-left: 0em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const FirstRow = styled(motion.div)`
  display: grid;
  grid-template-rows: 1fr;
`;

const Title = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  white-space: nowrap;
  font-size: 1.6em;
  line-height: 1.6em;
  font-weight: 700;
  margin: 0 2em 0 0;
  @media screen and (max-width: ${responsive.mobile}px) {
    font-size: 1.2em;
    margin: 0 auto;
  }
  @media screen and (max-width: ${responsive.tablet}px) {
    margin-bottom: 0;
  }
`;

const Subtitle = styled(motion.div)`
  font-size: 2em;
  line-height: 2em;
  font-weight: 700;
  align-self: center;
  width: fit-content;
  margin: ${({ collapseFirstRow }) =>
    collapseFirstRow ? '0 auto 0 0' : 'unset'};
  @media screen and (max-width: ${responsive.mobile}px) {
    font-size: 1.4em;
    margin: 0 auto;
  }
`;

const SecondRow = styled(({ collapseFirstRow, open, ...rest }) => (
  <div {...rest} />
))`
  font-size: 1.2em;
  line-height: 1.2em;
  display: flex;
  justify-content: flex-start;
  align-items: ${({ collapseFirstRow }) =>
    collapseFirstRow ? 'flex-end' : 'flex-start'};
  gap: 1ch;
  & > * {
    align-self: flex-start;
  }
  @media screen and (max-width: 1200px) and (min-width: 1024px) {
    // font-size: 0.8em;
  }
  @media screen and (max-width: ${responsive.tablet}px) {
    margin-top: 0;
  }
  @media screen and (max-width: ${responsive.mobile}px) {
    font-size: 1em;
  }
`;

export default DataDonut;
